import { ArrowLeft, CheckCircle2, Loader2, RefreshCcw, Send } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  evaluateAiGrowthTaskAnswer,
  generateAiGrowthTaskQuestion,
  getAiGrowthTasks,
  getLatestAiGrowthPlan,
  updateAiGrowthTask,
} from '../../api.js'
import { buildStudentRoadmap, completeLearningStep } from '../../student-roadmap.js'

function findNode(plan, nodeId) {
  for (const stage of buildStudentRoadmap(plan)) {
    const step = (stage.steps || []).find((item) => String(item.id) === String(nodeId))
    if (step) return { stage, step }
  }
  return null
}

function AiLearningExercisePage() {
  const { aiNodeId } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const requestedTaskId = searchParams.get('taskId') || ''
  const [context, setContext] = useState(null)
  const [question, setQuestion] = useState(null)
  const [answer, setAnswer] = useState('')
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(true)
  const [working, setWorking] = useState(false)
  const [completed, setCompleted] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    Promise.all([getLatestAiGrowthPlan(controller.signal), getAiGrowthTasks(controller.signal)])
      .then(([planPayload, taskPayload]) => {
        const plan = planPayload?.plan
        const found = findNode(plan, aiNodeId)
        const task = (taskPayload?.tasks || []).find((item) => (
          String(item.id) === String(requestedTaskId) || String(item.nodeId) === String(aiNodeId)
        ))
        if (!plan || !found || !task) throw new Error('当前 AI 练习不存在，请从学习节点重新进入。')
        setContext({ plan, task, ...found })
        setCompleted(task.status === 'completed')
        return generateAiGrowthTaskQuestion(task.id, controller.signal)
      })
      .then((payload) => {
        if (payload?.mode === 'code' && payload.practiceTaskId) {
          navigate(`/learning/practice/${encodeURIComponent(payload.practiceTaskId)}?fromAiNode=${encodeURIComponent(aiNodeId || '')}&aiTaskId=${encodeURIComponent(payload.taskId || '')}`, { replace: true })
          return
        }
        setQuestion(payload)
      })
      .catch((err) => {
        if (err.name !== 'AbortError') setError(err.message || 'AI 练习加载失败，请稍后重试。')
      })
      .finally(() => setLoading(false))
    return () => controller.abort()
  }, [aiNodeId, navigate, requestedTaskId])

  const criteria = useMemo(() => context?.step?.acceptanceCriteria || [], [context])

  async function changeQuestion() {
    if (!context?.task?.id) return
    setWorking(true)
    setError('')
    try {
      setQuestion(await generateAiGrowthTaskQuestion(context.task.id))
      setAnswer('')
      setResult(null)
    } catch (err) {
      setError(err.message || '暂时无法生成新问题。')
    } finally {
      setWorking(false)
    }
  }

  async function submitAnswer(event) {
    event.preventDefault()
    if (!context?.task?.id || !question?.question) return
    setWorking(true)
    setError('')
    try {
      setResult(await evaluateAiGrowthTaskAnswer(context.task.id, { question: question.question, answer }))
    } catch (err) {
      setError(err.message || 'AI 批改失败，请稍后重试。')
    } finally {
      setWorking(false)
    }
  }

  async function finishExercise() {
    if (!result?.passed || !context?.task?.id) return
    setWorking(true)
    setError('')
    try {
      await updateAiGrowthTask(context.task.id, { action: 'complete', note: `AI练习得分：${result.score}` })
      completeLearningStep(context.plan, context.stage, context.step)
      setCompleted(true)
    } catch (err) {
      setError(err.message || '练习已通过，但完成状态保存失败，请重试。')
    } finally {
      setWorking(false)
    }
  }

  return (
    <main className="page-container student-roadmap-page learning-ai-exercise-page">
      <section className="student-roadmap-stage-toolbar">
        <Link className="secondary-button" to={`/learning/ai/${encodeURIComponent(aiNodeId || '')}`}><ArrowLeft size={16} /> 返回学习节点</Link>
        <Link className="secondary-button" to="/today">今日任务</Link>
      </section>

      <section className="learning-ai-exercise-hero">
        <span>AI 专属练习</span>
        <h1>{context?.step?.title || '正在准备练习...'}</h1>
        <p>这里不是让你背答案。AI会根据当前节点追问理解、应用和判断，并针对你的回答给出反馈。</p>
      </section>

      {error ? <div className="student-growth-error"><strong>练习暂时无法继续</strong><p>{error}</p></div> : null}
      {loading ? <section className="learning-ai-exercise"><Loader2 size={20} /><strong>AI 正在根据节点生成问题...</strong></section> : null}

      {!loading && question ? (
        <form className="learning-ai-exercise" onSubmit={submitAnswer}>
          <div className="learning-ai-exercise-head">
            <span>AI 教练提问</span>
            <button className="secondary-button" type="button" onClick={changeQuestion} disabled={working}><RefreshCcw size={15} /> 换一道题</button>
          </div>
          <h2>{question.question}</h2>
          {question.context ? <p>{question.context}</p> : null}
          {criteria.length ? <aside><strong>本节点验收要求</strong><ul>{criteria.map((item) => <li key={item}>{item}</li>)}</ul></aside> : null}
          <label htmlFor="learning-ai-answer">你的回答</label>
          <textarea id="learning-ai-answer" value={answer} onChange={(event) => setAnswer(event.target.value)} placeholder="用自己的话回答，说明原因、步骤或实际应用。" rows={9} />
          <button className="primary-button" type="submit" disabled={working || answer.trim().length < 5}>{working ? <Loader2 size={16} /> : <Send size={16} />} 交给 AI 批改</button>

          {result ? (
            <section className={`learning-ai-feedback ${result.passed ? 'is-passed' : ''}`}>
              <strong>{result.score} 分 · {result.passed ? '理解达标' : '还需要补充'}</strong>
              <p>{result.feedback}</p>
              {result.strengths?.length ? <p>答得好的地方：{result.strengths.join('；')}</p> : null}
              {result.gaps?.length ? <p>需要补充：{result.gaps.join('；')}</p> : null}
              <details><summary>查看参考答案</summary><p>{result.modelAnswer}</p></details>
              {result.nextAction ? <p>下一步：{result.nextAction}</p> : null}
              {result.passed && !completed ? <button className="primary-button" type="button" onClick={finishExercise} disabled={working}><CheckCircle2 size={16} /> 通过并完成节点</button> : null}
              {completed ? <p><CheckCircle2 size={16} /> 已同步到今日任务和成长进度。</p> : null}
            </section>
          ) : null}
        </form>
      ) : null}
    </main>
  )
}

export default AiLearningExercisePage
